import { css } from "@linaria/core"
import { Stack, Theme, Typography, useMediaQuery } from "@mui/material"

import { useTheme } from "@mui/material"
import { Plus } from "@untitled-ui/icons-react"
import { motion } from "framer-motion"
import numbro from "numbro"
import { Link } from "react-router-dom"

import PoolShare from "./PoolShare"
import { PoolOverview } from "../../ao/router"
import { useTokenInfo } from "../../hooks/use-token-info"
import { truncateId } from "../../utils/data-utils"
import { formatTicker } from "../../utils/format"
import { CopyToClipboard } from "../CopyToClipboard"
import FeeDisplay from "../FeeDisplay"
import PoolImage from "../PoolImage"

import sparkleImage from "/sparkle.svg"

import ActionButton from "../ui/ActionButton"

type Props = {
  pool: PoolOverview
  handlePoolShare: (poolId: string, userShares: number) => void
}

function PoolsTableRow(props: Props) {
  const { pool, handlePoolShare } = props
  const theme = useTheme()

  const [tokenA] = useTokenInfo(pool.base_token_process)
  const [tokenB] = useTokenInfo(pool.quote_token_process)

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  if (isMobile) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          border: `1px solid ${theme.palette.enhanced.main}`,
          padding: "16px",
          marginBottom: "16px",
          borderRadius: "8px",
        }}
        className={css`
          font-weight: 700;
          font-size: 16px;
          color: var(--mui-palette-text-secondary);

          width: calc(100vw - 56px);
        `}
      >
        <Stack alignItems="center" justifyContent="space-between" direction="row">
          <Link
            to={`/swap?from=${pool.quote_token_process}&to=${pool.base_token_process}`}
            style={{
              color: theme.palette.text.primary,
              textDecoration: "none",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <PoolImage tokenA={tokenA} tokenB={tokenB} />
              <Typography variant="h6">
                {formatTicker(tokenA?.ticker)}/{formatTicker(tokenB?.ticker)}
              </Typography>
            </Stack>
          </Link>
          <Typography variant="inherit"># {pool.tvl_rank}</Typography>
        </Stack>

        <Stack>
          <Typography
            sx={{
              marginLeft: "41px",
              fill: "var(--mui-palette-text-secondary)",
              "& div": {
                height: 18,
                verticalAlign: "middle",
              },
              fontFamily: "monospace",
            }}
            variant="body2"
            component={"div"}
          >
            {truncateId(pool.amm_process)}
            <CopyToClipboard value={pool.amm_process} />
          </Typography>
          <FeeDisplay feebps={pool.pool_fee_bps} />
        </Stack>

        <Stack gap={0.5} marginY={1}>
          <Stack alignItems="center" justifyContent="space-between" direction="row">
            <Typography variant="body2" component={"span"}>
              Transactions:
            </Typography>{" "}
            <span>{pool.tx_count || 0}</span>
          </Stack>

          <Stack alignItems="center" justifyContent="space-between" direction="row">
            <Typography variant="body2">30 day APY:</Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                background: theme.palette.enhanced.main,
                py: 0.5,
                px: 1,
                borderRadius: "4px",
              }}
            >
              <span>{numbro(pool.apr_30d ?? 0).format({ output: "percent", mantissa: 2 })}</span>
              <img src={sparkleImage} alt="sparkles" />
            </Stack>
          </Stack>

          <Stack alignItems="center" justifyContent="space-between" direction="row">
            <Typography variant="body2" component={"span"}>
              Your Total Pool Share:
            </Typography>{" "}
            <PoolShare
              poolId={pool.amm_process}
              tvl={pool.tvl_in_usd}
              handlePoolShare={handlePoolShare}
            />
          </Stack>
        </Stack>

        <Stack
          gap={1}
          direction="row"
          className={css`
            justify-content: flex-end;

            & > a {
              font-size: 12px;
              font-weight: 700;
              color: var(--mui-palette-text-secondary) !important;
              text-decoration: none;
              display: flex;
              align-items: center;
              justify-content: space-around;
            }
          `}
        >
          <Link
            to={`/add?tokenA=${pool.base_token_process}&tokenB=${pool.quote_token_process}&fee=${pool.pool_fee_bps}`}
          >
            <ActionButton icon={<Plus style={{ height: 18, width: 18 }} />} variant="accent">
              Add
            </ActionButton>
          </Link>
          <Link to={`/remove?pool=${pool.amm_process}`}>
            <ActionButton>Withdraw</ActionButton>
          </Link>
        </Stack>
      </motion.div>
    )
  }

  return (
    <motion.tr
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        border: `1px solid ${theme.palette.enhanced.main}`,
      }}
      className={css`
        & > td {
          padding: calc(var(--spacing) * 2) var(--spacing);
          font-weight: 700;
          font-size: 16px;
          color: var(--mui-palette-text-secondary);
        }
      `}
    >
      <td>{pool.tvl_rank}</td>
      <td>
        <Link
          to={`/swap?from=${pool.quote_token_process}&to=${pool.base_token_process}`}
          style={{
            color: theme.palette.text.primary,
            textDecoration: "none",
          }}
        >
          <Stack gap={0.5} direction="row" alignItems="center">
            <PoolImage tokenA={tokenA} tokenB={tokenB} />
            <Stack gap={0.5} direction="row" alignItems="center">
              {formatTicker(tokenA?.ticker)}
            </Stack>
            /
            <Stack gap={0.5} direction="row" alignItems="center">
              {formatTicker(tokenB?.ticker)}
            </Stack>
          </Stack>
        </Link>
        <Stack direction="row" gap={0.5} alignItems="center">
          <Typography
            sx={{
              marginLeft: "35px",
              fill: "var(--mui-palette-text-secondary)",
              "& div": {
                height: 18,
                verticalAlign: "middle",
              },
              fontFamily: "monospace",
            }}
            variant="body2"
            component={"div"}
          >
            {truncateId(pool.amm_process)}
            <CopyToClipboard value={pool.amm_process} />
          </Typography>
          <FeeDisplay feebps={pool.pool_fee_bps} />
        </Stack>
      </td>
      <td
        align="right"
        className={css`
          font-family: "Space Grotesk";
          width: 9.375rem;
        `}
      >
        {pool.tx_count || 0}
      </td>
      <td
        align="right"
        className={css`
          font-family: "Space Grotesk";
        `}
      >
        <PoolShare
          poolId={pool.amm_process}
          tvl={pool.tvl_in_usd}
          handlePoolShare={handlePoolShare}
        />
      </td>
      <td style={{ width: "120px" }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            background: theme.palette.enhanced.main,
            py: 1,
            px: 1,
            borderRadius: "4px",
          })}
        >
          <span>{numbro(pool.apr_30d ?? 0).format({ output: "percent", mantissa: 2 })}</span>
          <img src={sparkleImage} alt="sparkles" />
        </Stack>
      </td>
      <td align="right">
        <Stack
          gap={1}
          direction="row"
          className={css`
            justify-content: flex-end;

            & > a {
              font-size: 12px;
              font-weight: 700;
              color: var(--mui-palette-text-secondary) !important;
              text-decoration: none;
              display: flex;
              align-items: center;
              justify-content: space-around;
            }
          `}
        >
          <Link
            to={`/add?tokenA=${pool.base_token_process}&tokenB=${pool.quote_token_process}&fee=${pool.pool_fee_bps}`}
          >
            <ActionButton icon={<Plus style={{ height: 18, width: 18 }} />} variant="accent">
              Add
            </ActionButton>
          </Link>
          <Link to={`/remove?pool=${pool.amm_process}`}>
            <ActionButton>Withdraw</ActionButton>
          </Link>
        </Stack>
      </td>
    </motion.tr>
  )
}

export default PoolsTableRow
